import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://tcio-api.onrender.com';

export default function DueTodayPanel({ token, facilityId, compact, onOpenPatient, onFeed }) {
  const [feed, setFeed] = useState(null);
  const [tab, setTab] = useState('today');
  const [sort, setSort] = useState('urgency');
  const [savingDue, setSavingDue] = useState(null);
  const [missing, setMissing] = useState(null);
  const loadFeed = useCallback(async () => {
    try {
      const q = facilityId ? ('?facilityId=' + facilityId) : '';
      const r = await fetch(API + '/api/ur/due-today' + q, { headers: { Authorization: 'Bearer ' + token } });
      const j = await r.json();
      if (j.ok) { setFeed(j.data); if (onFeed) onFeed(j.data); }
    } catch (e) {}
  }, [token, facilityId]);
  useEffect(() => { loadFeed(); }, [loadFeed]);
  if (!feed) return null;
  const dueSoon = feed.review_due.filter(r => r.days_overdue <= 1);
  const dueIds = new Set(dueSoon.map(r => r.ur_patient_id));
  const todayRows = dueSoon.concat(feed.new_admits.filter(r => !dueIds.has(r.ur_patient_id)));
  const backlog = feed.review_due.filter(r => r.days_overdue > 1);
  const buckets = {
    today: { label: 'Due Today (' + todayRows.length + ')', rows: todayRows, color: '#b45309' },
    admits: { label: 'New Admits (' + feed.new_admits.length + ')', rows: feed.new_admits, color: '#2563eb' },
    pending: { label: 'Pending Payer (' + (feed.pending || []).length + ')', rows: feed.pending || [], color: '#7c3aed' },
    lapsed: { label: 'Coverage Lapsed (' + feed.expiring.length + ')', rows: feed.expiring, color: '#dc2626' },
    backlog: { label: 'Overdue Backlog (' + backlog.length + ')', rows: backlog, color: '#64748b' },
  };
  const rows = [...(buckets[tab].rows || [])];
  if (sort === 'name') rows.sort((a, b) => (a.patient_name || '').localeCompare(b.patient_name || ''));
  else if (sort === 'admit') rows.sort((a, b) => new Date(b.admit_date) - new Date(a.admit_date));
  const setNextDue = async (r) => {
    const dflt = new Date(Date.now() + 6 * 86400000).toISOString().slice(0, 10);
    const v = window.prompt('Next review date for ' + r.patient_name + ' (YYYY-MM-DD):', dflt);
    if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
    setSavingDue(r.ur_patient_id);
    try {
      await fetch(API + '/api/ur/' + r.ur_patient_id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ ur_due: v }),
      });
      loadFeed();
    } catch (e) { alert('Failed to save'); }
    setSavingDue(null);
  };
  const lim = compact ? 8 : 40;
  const openMissing = async () => {
    try {
      const q = facilityId ? ('?facilityId=' + facilityId) : '';
      const r = await fetch(API + '/api/ur/missing-records' + q, { headers: { Authorization: 'Bearer ' + token } });
      const j = await r.json();
      setMissing(j.ok ? j.data : []);
    } catch (e) { setMissing([]); }
  };
  const addToUr = async (m) => {
    try {
      const r = await fetch(API + '/api/ur/create-record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ episode_id: m.episode_id }),
      });
      const j = await r.json();
      if (!j.ok) { alert(j.error || 'Could not add this client to UR'); return; }
      setMissing(prev => (prev || []).filter(x => x.episode_id !== m.episode_id));
      loadFeed();
    } catch (e) { alert('Could not add this client to UR'); }
  };
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{compact ? 'UR — Today' : "Today's Work"}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(buckets).map(([k, b]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                border: '1px solid ' + (tab === k || (k === 'admits' && b.rows.length > 0) ? b.color : '#e2e8f0'),
                background: tab === k ? b.color : ((k === 'admits' && b.rows.length > 0) ? '#eff6ff' : '#fff'), color: tab === k ? '#fff' : ((k === 'admits' && b.rows.length > 0) ? b.color : '#334155') }}>
              {b.label}
            </button>
          ))}
          {!compact && (
            <button onClick={openMissing}
              style={{ padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '1px dashed #94a3b8', background: '#fff', color: '#334155' }}>
              + Add client to UR
            </button>
          )}
          {!compact && (
            <select value={sort} onChange={e => setSort(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}>
              <option value="urgency">Most urgent</option>
              <option value="name">A to Z</option>
              <option value="admit">Newest admit</option>
            </select>
          )}
        </div>
      </div>
      {missing && (
        <div style={{ marginTop: 10, padding: '10px 12px', border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{'Admitted clients with no UR record (' + missing.length + ')'}</div>
            <button onClick={() => setMissing(null)} style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>Close</button>
          </div>
          {missing.length === 0 ? (
            <div style={{ fontSize: 13, color: '#475569' }}>Every admitted client here already has a UR record.</div>
          ) : missing.map(m => (
            <div key={m.episode_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: '#0f172a' }}>
                <span style={{ fontWeight: 700 }}>{m.patient_name || 'Unnamed client'}</span>
                <span style={{ color: '#64748b', marginLeft: 8 }}>{[m.facility_name, m.loc, m.primary_payer, m.admit_date ? 'adm ' + m.admit_date : null].filter(Boolean).join(' - ')}</span>
              </div>
              <button onClick={() => addToUr(m)} style={{ padding: '4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 6, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer' }}>Add to UR</button>
            </div>
          ))}
        </div>
      )}
      {rows.length === 0 ? (
        <div style={{ padding: '14px 4px', fontSize: 13, color: '#64748b' }}>Nothing in this bucket. Clear.</div>
      ) : (
        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
          {rows.slice(0, lim).map(r => (
            <div key={r.ur_patient_id + tab} onClick={() => onOpenPatient && onOpenPatient(r)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px',
                border: '1px solid #f1f5f9', borderRadius: 8, cursor: onOpenPatient ? 'pointer' : 'default', background: '#fafbfc', flexWrap: 'wrap', gap: 6 }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{r.patient_name}</span>
                <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                  {(r.loc || '-') + ' · ' + (r.primary_payer || 'no payer') + (compact ? ' · ' + (r.facility_name || '') : '') + ' · adm ' + String(r.admit_date).slice(0, 10)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {tab === 'lapsed' && <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>{r.days_uncovered}d uncovered</span>}
                {(tab === 'today' || tab === 'backlog') && r.days_overdue != null && <span style={{ fontSize: 12, fontWeight: 700, color: tab === 'backlog' ? '#64748b' : '#b45309' }}>{r.days_overdue}d overdue</span>}
                {tab === 'pending' && <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{'submitted ' + (r.submitted || '') + ' · ' + r.days_waiting + 'd waiting'}</span>}
                {tab === 'admits' && <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb' }}>{'day ' + r.days_since_admit + (r.submitted ? ' · submitted ' + r.submitted : ' · no UR touch')}</span>}
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{r.assigned_name || 'unassigned'}</span>
                <button onClick={e => { e.stopPropagation(); setNextDue(r); }} disabled={savingDue === r.ur_patient_id}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                  {savingDue === r.ur_patient_id ? '...' : 'Set next review'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
